import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { RootState, ShortUser, UUID } from '@umanforge/common';

import { TaskFull } from 'interfaces/task.interface';
import ActionTile from 'features/task_modal/components/actionTile.component';
import { WarningIcon } from 'components/icons/warning_icon.component';
import { ActionFull } from 'interfaces/action.interface';
import { ActionService } from 'services/action.service';
import { SectorSettings } from 'interfaces/setting.interface';
import { SettingService } from 'services/setting.service';
import { Tooltip } from 'react-tooltip';

interface ActionFormProps {
  task: TaskFull;
  users: ShortUser[];
  onChange: () => void;
  isAdmin: boolean;
  showValidateAllButton?: boolean;
  sectorId: string;
}

interface ActionItem {
  action: ActionFull | undefined;
  user: ShortUser;
}

const ActionForm: React.FC<ActionFormProps> = ( {
  task,
  users,
  onChange,
  isAdmin = false,
  showValidateAllButton = false,
  sectorId
} ) => {
  const { t } = useTranslation();
  const token = useSelector( ( state: RootState ) => {return state.token.value;} );
  const dispatch = useDispatch();
  const me = useSelector( ( state: RootState ) => {return state.user.value;} );

  const [ isFirstRender, setIsFirstRender ] = useState( true );

  const [ actions, setActions ] = useState<ActionItem[]>( [] );
  const [ settings, setSettings ] = useState<SectorSettings>();

  // State for validate all button
  const [ validateAllLoading, setValidateAllLoading ] = useState( false );
  const [ validateAllError, setValidateAllError ] = useState<string | null>( null );
  const [ validateAllSuccess, setValidateAllSuccess ] = useState<string | null>( null );
  const [ selectedActionId, setSelectedActionId ] = useState<UUID | undefined>( undefined );

  const [ allActionValidated, setAllActionValidated ] = useState<boolean>( false );
  const [ cannotValidateAll, setCannotValidateAll ] = useState<boolean>( true );


  // External data
  const fetchSettings = async () => {
    setSettings( await SettingService.getSectorSettingsBySector( sectorId, token, dispatch ) );
  };

  // External data
  const fetchActions = async () => {
    const _actions = await ActionService.getActionsFullByTaskId( task.id, token, dispatch );

    const actionsItem: ActionItem[] = [];
    users.forEach( user => {
      const action = _actions.find( act => {
        return act.idUser == user.id;
      } );
      actionsItem.push( { action: action ?? undefined, user: user } );
    } );


    setActions( actionsItem );
  };
  // Use Effects
  useEffect( () => {
    if ( !isFirstRender ) {
      if ( task ) {
        fetchActions();
        fetchSettings();
      }
    }
  }, [ isFirstRender ] );

  useEffect( () => {
    setIsFirstRender( false );
  }, [ ] );

  const handleValidateAll = async () => {
    if ( isAdmin ) {
      setValidateAllLoading( true );
      setValidateAllError( null );
      setValidateAllSuccess( null );
      try {
        await ActionService.validateActionsByTaskId( task.id, token, dispatch );
        await fetchActions();
        onChange();
        setValidateAllSuccess( t( 'all_actions_completed' ) );
      } catch ( error ) {
        let errorMsg = t( 'validate_all_error' ) ;
        if ( error && typeof error === 'object' && 'message' in error ) {
          errorMsg += `: ${( error as unknown as { message?: string } ).message ?? ''}`;
        }
        setValidateAllError( errorMsg );
      } finally {
        setValidateAllLoading( false );
      }
    }
  };

  const onActionChange = async () => {
    await fetchActions();
    onChange();
  };

  useEffect( () => {
    const allValidated = actions.every( a => { return a.action != null && a.action.dateEnd != null; } ) || task.status == 'DONE';
    const cannotValidate = validateAllLoading || actions.length === 0 || allValidated;
    setAllActionValidated( allValidated );
    setCannotValidateAll( cannotValidate );
  }, [ actions ] );

  return (
    <>
      { actions.length == 0 && (
        <div className="flex flex-col items-center justify-center h-full">
          <WarningIcon size={48} className="mb-4 fill-orange-500" />
          <div className='flex'>
            <span className="text-lg font-semibold text-gray-600">{ t( 'no_actions_to_validate' ) }</span>
            <span
              id={'action-info-tooltip'}
              className={'ml-2 cursor-pointer flex items-center justify-center rounded-full bg-gray-200/60 w-6 h-6 text-gray-700 text-sm font-bold'}
            > i </span>
            <Tooltip
              anchorSelect={'#action-info-tooltip'}
              className={'z-[1000] max-w-[400px] text-wrap'}
              place="top"
              positionStrategy='fixed'
            >
              <span>
                <span className="text-[14px] font-semibold underline underline-offset-2">{ t( 'translation:no_actions_to_validate' ) }</span>
                <p className="pl-2 pt-1">{ `${t( 'translation:no_actions_to_validate_description' )}\n${ task.status == 'DONE' ? '' : t( 'translation:task_will_complete_automatically' )}` }</p>
              </span>
            </Tooltip>
          </div>
        </div>
      ) }

      { actions.map( actionItem => {
        return (
          <div key={ actionItem.user.id + '_' + actionItem.action?.notes.map( n => { return n.id + '_' + n.content + '_' + n.disabled; } ).join( '-' ) } className='mb-2'>
            <ActionTile
              user={ actionItem.user }
              action={ actionItem.action }
              disable={ false }
              selected={ selectedActionId == actionItem.user.id }
              setSelected={ setSelectedActionId }
              onChange={ onActionChange }
              settings={settings}
              task={task}
              className='w-full border'
              isAdmin={ isAdmin || me?.id == actionItem.user.id }
            />
          </div>
        );
      } ) }

      { isAdmin && showValidateAllButton && (
        <>
          <div
            id={ 'validate_all_button' }
            className={ `h-10 w-fit pr-2 pl-1 bg-blue-300 flex items-center rounded-lg ${ cannotValidateAll ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-300/80' }` }
            onClick={ cannotValidateAll ? undefined : handleValidateAll }
            style={ { pointerEvents: cannotValidateAll ? 'none' : 'auto' } }
            aria-disabled={ cannotValidateAll }
          >
            { !allActionValidated ? <WarningIcon size={ 30 } className='mx-2 fill-orange-500 ' /> : <div className='ml-0.5'/> }
            <label htmlFor={ 'validate_all_button' } className='w-full h-fit text-center justify-center'>
              { validateAllLoading ? (
                <span className='flex items-center'>
                  <span className='loader mr-2' style={{ width: 18, height: 18, border: '2px solid #fff', borderTop: '2px solid #3b82f6', borderRadius: '50%', display: 'inline-block', animation: 'spin 1s linear infinite' }} />
                  { t( 'validating' ) }
                </span>
              ) : actions.length === 0 ? t( 'no_actions_to_validate' ) : allActionValidated ? t( 'all_actions_completed' ) : t( 'validate_all_actions' ) }
            </label>
          </div>
          { validateAllError && <div className='text-red-500 mt-2'>{ validateAllError }</div> }
          { validateAllSuccess && <div className='text-green-600 mt-2'>{ validateAllSuccess }</div> }
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </>
      ) }
    </>
  );
};

export default ActionForm;
